// ds-color-ignore-file: system font stack in dev/internal file
"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import dynamic from "next/dynamic"
import {
  createSsoBridge,
  hasStaffSsoSignal,
  tokenHasInternalAccess,
  type SsoBridge,
} from "@ahead-health/core/ui/staffSso"

// The cockpit (inline editor + review worklist) only ever renders for signed-in
// staff, so it stays out of the public bundle: this dynamic import fires only
// after sign-in is confirmed below. Anonymous visitors never request the chunk.
const Cockpit = dynamic(
  () => import("./cockpit").then((m) => ({ default: m.Cockpit })),
  { ssr: false }
)

const SYSTEM_SERVER_URL = "https://system.aheadhealth.dev"

type StaffEnvironment = "prod" | "preflight" | "staging" | "feature-branch"

function environmentForBranchName(branch: string): StaffEnvironment {
  if (branch === "main" || branch === "master") return "prod"
  if (branch === "preflight") return "preflight"
  if (branch === "develop" || branch === "staging") return "staging"
  return "feature-branch"
}

/**
 * Branch → environment, mirroring ahead-core's `getEnvironment` (which the old
 * toolbar wrapper used to fill `identity.environment`): release-
 * promotion (`release/<from>-to-<to>`) and hotfix (`hotfix/<to>/<slug>`)
 * branches deploy AS their target lane, everything else maps by name.
 * (dev-editor-switcher.tsx has no shared helper — the dev lane hardcodes
 * "development" — so the SSO path keeps the branch-derived value here.)
 */
function environmentForBranch(branch: string): StaffEnvironment {
  const release =
    /^release\/(?:\d{4}-\d{2}-\d{2}\/)?(?:develop|staging|preflight|main)-to-(develop|staging|preflight|main)$/.exec(
      branch
    )
  if (release) return environmentForBranchName(release[1])
  const hotfix = /^hotfix\/(develop|staging|preflight|main)\//.exec(branch)
  if (hotfix) return environmentForBranchName(hotfix[1])
  return environmentForBranchName(branch)
}

type Phase =
  | "checking" // pre-mount; renders null
  | "none" // no staff signal — render null forever
  | "loading" // signal present, awaiting the first token read
  | "signed-out" // signal but no live token — sign-in button
  | "unauthorized" // token without internal access — re-auth button
  | "signed-in"

const fabStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 12,
  left: 12,
  zIndex: 2147483647,
  fontFamily: "ui-sans-serif, system-ui",
  fontSize: 12,
  fontWeight: 700,
  color: "#fff",
  background: "#0b0b0f",
  border: "none",
  borderRadius: 999,
  padding: "8px 14px",
  cursor: "pointer",
  boxShadow: "0 6px 18px rgba(0,0,0,.25)",
}

/**
 * Lazy staff-only gate for the inline editor + review cockpit. Replaces the
 * old wrapper that mounted ahead-core's feedback toolbar on every pageview
 * (and with it an SSO-bridge iframe → Firebase Auth, ~93 KiB — for anonymous
 * visitors too). This gate checks for a staff signal first (persisted SSO
 * token, launcher hash, or a `review-cockpit`/`review` deep link); without one
 * it renders null forever — no bridge, no iframe, no cockpit chunk.
 */
export function StaffEditorGate() {
  const [phase, setPhase] = useState<Phase>("checking")
  const [idToken, setIdToken] = useState<string | null>(null)
  // Edit mode on by default once signed in — it's the most-used action.
  const [editing, setEditing] = useState(true)
  const bridgeRef = useRef<SsoBridge | null>(null)

  const applyToken = useCallback((t: { idToken: string } | null) => {
    if (!t) {
      setIdToken(null)
      setPhase("signed-out")
    } else if (tokenHasInternalAccess(t.idToken)) {
      setIdToken(t.idToken)
      setPhase("signed-in")
    } else {
      setIdToken(null)
      setPhase("unauthorized")
    }
  }, [])

  useEffect(() => {
    if (!hasStaffSsoSignal()) {
      setPhase("none")
      return
    }
    const bridge = createSsoBridge(SYSTEM_SERVER_URL)
    bridgeRef.current = bridge
    setPhase("loading")
    let cancelled = false
    void bridge.getToken().then((t) => {
      if (!cancelled) applyToken(t)
    })
    // Keep the idToken fresh: fires on proactive refresh and with null when a
    // scheduled refresh finds the session gone (→ back to the sign-in button).
    const unsubscribe = bridge.subscribe((t) => {
      if (!cancelled) applyToken(t)
    })
    return () => {
      cancelled = true
      unsubscribe()
      bridge.destroy()
      bridgeRef.current = null
    }
  }, [applyToken])

  const signIn = useCallback(() => {
    const bridge = bridgeRef.current
    if (!bridge) return
    setPhase("loading")
    void bridge.signInPopup().then(applyToken)
  }, [applyToken])

  // Anonymous visitors (and the pre-mount / token-resolving window): nothing.
  if (phase === "checking" || phase === "none" || phase === "loading") {
    return null
  }

  if (phase === "signed-in" && idToken) {
    return (
      <>
        <Cockpit
          active={editing}
          identity={{
            idToken,
            environment: environmentForBranch(
              process.env.NEXT_PUBLIC_AHEAD_BRANCH ?? ""
            ),
            branch: process.env.NEXT_PUBLIC_AHEAD_BRANCH,
          }}
          // Leave edit mode from inside the cockpit head, so the only exit
          // isn't buried in a toggle.
          onExit={() => setEditing(false)}
        />
        {!editing ? (
          <button
            type="button"
            style={fabStyle}
            data-ahead-editor-ui
            onClick={() => setEditing(true)}
          >
            Edit content
          </button>
        ) : null}
      </>
    )
  }

  return (
    <button
      type="button"
      style={fabStyle}
      data-ahead-editor-ui
      onClick={signIn}
    >
      {phase === "unauthorized"
        ? "No editor access — sign in with another account"
        : "Sign in to edit"}
    </button>
  )
}
